import { AnalyticsContentType, MediaTypes } from '@setplex/tria-api'
import { attach, sample } from 'effector'
import { and } from 'patronum'
import type { FavoriteClickPayload } from '~/features/favorites/index.h'
import { handleIsFavorite } from '~/features/favorites/model'
import type { SendEventParams } from '~/shared/analytix'
import { logErrorFx } from '../lib'
import { $readyAllInfo } from '../session/model'
import { $analytix, $analytixEnabled } from '../wrapper'

const $favoriteEventCanBeSent = and($readyAllInfo, $analytixEnabled)

const contentTypeToAnalytics = (
  contentType: MediaTypes
): AnalyticsContentType => {
  switch (contentType) {
    case MediaTypes.VIDEO:
      return AnalyticsContentType.movie
    case MediaTypes.LIVE_CHANNEL:
      return AnalyticsContentType.live
    case MediaTypes.TV_SHOW:
      return AnalyticsContentType.tvshow
  }
}

const favoriteClickPayloadToCreatorId = (p: FavoriteClickPayload): string =>
  String(p.publisherId ?? p.creatorId ?? '')

const toFavoritesAddPayload = (
  p: FavoriteClickPayload,
  itemType: AnalyticsContentType
): SendEventParams.FavoritesAdd => ({
  itemId: String(p.id),
  itemType,
  title: p.contentTitle ?? '',
  creatorId: favoriteClickPayloadToCreatorId(p),
})

const toFavoritesRemovePayload = (
  p: FavoriteClickPayload,
  itemType: AnalyticsContentType
): SendEventParams.FavoritesRemove => ({
  itemId: String(p.id),
  itemType,
  title: p.contentTitle ?? '',
  creatorId: favoriteClickPayloadToCreatorId(p),
})

export const sendFavoritesAddFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.FavoritesAdd) =>
    analytix.sendFavoritesAddEvent(params),
})

export const sendFavoritesRemoveFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.FavoritesRemove) =>
    analytix.sendFavoritesRemoveEvent(params),
})

sample({
  clock: sendFavoritesAddFx.failData,
  fn: (e) => e,
  target: logErrorFx,
})

sample({
  clock: sendFavoritesRemoveFx.failData,
  fn: (e) => e,
  target: logErrorFx,
})

sample({
  clock: handleIsFavorite,
  source: $favoriteEventCanBeSent,
  filter: (canSend, { isFavorite }) => Boolean(canSend) && isFavorite,
  fn: (_, item) =>
    toFavoritesAddPayload(item, contentTypeToAnalytics(item.contentType)),
  target: sendFavoritesAddFx,
})

sample({
  clock: handleIsFavorite,
  source: $favoriteEventCanBeSent,
  filter: (canSend, { isFavorite }) => Boolean(canSend) && !isFavorite,
  fn: (_, item) =>
    toFavoritesRemovePayload(item, contentTypeToAnalytics(item.contentType)),
  target: sendFavoritesRemoveFx,
})
